import { buildDiscoveryUrl } from './buildDiscoveryUrl.js';
import { STYLE_DISCOVERY_PROVIDERS, getVisibleProviderIds } from './providers.js';

export const ALLOWED_DISCOVERY_CATEGORIES = new Set([
  'top', 'bottom', 'outer', 'shoes', 'accessory', 'eyewear',
]);

function cleanString(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

export function selectLocalizedValue({ language, ko, en, fallback = '' }) {
  const isEnglish = String(language || '').startsWith('en');
  const preferred = isEnglish ? cleanString(en) : cleanString(ko);
  return preferred || cleanString(en) || cleanString(ko) || cleanString(fallback);
}

export function normalizeDiscoveryItems(items, language = 'ko') {
  if (!Array.isArray(items)) return [];

  const normalized = [];
  const seen = new Set();
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const category = cleanString(item.category).toLowerCase();
    const itemType = cleanString(item.item_type);
    if (!ALLOWED_DISCOVERY_CATEGORIES.has(category) || !itemType) continue;

    const searchQueries = item.search_queries && typeof item.search_queries === 'object'
      ? item.search_queries
      : {};
    const query = selectLocalizedValue({
      language,
      ko: searchQueries.ko || item.search_query_ko,
      en: searchQueries.en || item.search_query_en,
    });
    if (!buildDiscoveryUrl({ providerId: 'pinterest', query })) continue;

    const displayName = selectLocalizedValue({
      language,
      ko: item.display_name_ko,
      en: item.display_name_en,
      fallback: itemType,
    });
    const dedupeKey = `${category}|${query.toLocaleLowerCase()}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    normalized.push({
      ...item,
      id: cleanString(item.id) || `${category}-${item.source_item_index ?? normalized.length}`,
      category,
      item_type: itemType,
      source_action: item.source_action === 'ADJUST' ? 'ADJUST' : 'REPLACE',
      query,
      displayName,
      attributes: item.attributes && typeof item.attributes === 'object' ? item.attributes : {},
      reason: cleanString(item.reason),
      expected_impact: cleanString(item.expected_impact),
      recommendation_source: cleanString(item.recommendation_source) || 'organic_search',
      is_sponsored: Boolean(item.is_sponsored),
    });
    if (normalized.length === 3) break;
  }
  return normalized;
}

export function getDiscoveryAttributes(attributes) {
  if (!attributes || typeof attributes !== 'object') return [];
  const candidates = [
    Array.isArray(attributes.colors) ? attributes.colors[0] : null,
    attributes.material,
    attributes.shape,
    Array.isArray(attributes.moods) ? attributes.moods[0] : null,
  ];
  const seen = new Set();
  return candidates.reduce((result, value) => {
    const token = cleanString(value);
    const key = token.toLocaleLowerCase();
    if (!token || seen.has(key) || result.length >= 3) return result;
    seen.add(key);
    result.push(token);
    return result;
  }, []);
}

export function buildDiscoveryLinks({ query, market, marketResolved }) {
  return getVisibleProviderIds({ market, marketResolved }).flatMap((providerId) => {
    const url = buildDiscoveryUrl({ providerId, query });
    return url ? [{ ...STYLE_DISCOVERY_PROVIDERS[providerId], url }] : [];
  });
}

export function buildDiscoveryClickAnalytics({ providerId, item, position, market }) {
  const provider = STYLE_DISCOVERY_PROVIDERS[providerId];
  return {
    analysis_type: 'outfit',
    provider: providerId,
    provider_type: provider?.kind || 'style_reference',
    category: item.category,
    item_type: item.item_type,
    source_action: item.source_action,
    position,
    market: market || 'UNKNOWN',
    recommendation_source: item.recommendation_source || 'organic_search',
    is_sponsored: Boolean(item.is_sponsored),
  };
}
